/* Identity Series builder — Collage and Wood
   Owns: size/digit selection, live pricing, jersey input, Cloudinary uploads,
   photo-delivery choice, senior-night date validation, add-to-cart.
   Reads config from #ic-config (JSON) rendered by the builder section. */
(function () {
  'use strict';

  var root = document.querySelector('[data-ic-builder]');
  if (!root) return;

  var cfgEl = document.getElementById('ic-config');
  var CFG = cfgEl ? JSON.parse(cfgEl.textContent) : {};
  var VARIANTS = CFG.variants || {};   // "15|single" -> { id, price }
  var SIZES = CFG.sizes || {};         // "15" -> { label, photos, note, tag }
  var STANDS = CFG.stands || {};       // "15" -> { id, price }
  var REF = makeRef();
  // 2026-10-03: milestone birthday pages reuse this builder. The number is fixed by the product
  // (CFG.fixedNumber, e.g. "80"), there's no Digit(s) picker, any day works for a party date,
  // and one stand ships per digit. Every branch below is gated on BDAY so sport pages are unchanged.
  var BDAY = CFG.mode === 'birthday';
  var BDAY_N = String(CFG.fixedNumber || '');

  // Grip height (canvas y, 460x500) per collage size -- hand-set by the designer, not a formula.
  // See "Scale Figure Spec" handoff, 2026-09-13.
  var SCALE_GRIP_Y = { 12: 248, 15: 258, 18: 270, 24: 288, 36: 300 };

  var state = {
    size: CFG.defaultSize || '15',
    digits: 'single',
    jersey: BDAY ? BDAY_N : '',
    date: '',
    share: '',
    stand: false,
    rush: false,
    uploads: [],
    busy: false
  };

  /* ---------- helpers ---------- */

  function makeRef() {
    var A = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789', r = '', i;
    for (i = 0; i < 4; i++) r += A.charAt(Math.floor(Math.random() * A.length));
    var d = new Date(), p = function (n) { return ('0' + n).slice(-2); };
    return 'CW-' + String(d.getFullYear()).slice(2) + p(d.getMonth() + 1) + p(d.getDate()) + '-' + r;
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2) + ' USD';
  }

  function variant() {
    return VARIANTS[state.size + '|' + state.digits] || null;
  }

  function isDouble() { return state.digits === 'double'; }

  function photoRange(size, dbl) {
    var raw = (SIZES[size] && SIZES[size].photos) || '20-25';
    if (!dbl) return raw;
    return raw.split('-').map(function (n) { return parseInt(n, 10) * 2; }).join('-');
  }

  function photoMin(size, dbl) {
    return parseInt(photoRange(size, dbl).split('-')[0], 10) || 0;
  }

  function digitsLabel() {
    var v = state.jersey.toUpperCase();
    // 2026-09-14: default matches whichever digit count is selected ("0" for Single, "00" for
    // Double) instead of always falling back to "00" -- that mismatch (showing "00" in the
    // preview/cart properties even with Single Digit selected) was the actual source of the
    // price-surprise confusion Crystal flagged, not the Digit(s) picker's position on the page.
    return v || (isDouble() ? '00' : '0');
  }

  function standQty() { return BDAY ? Math.max(1, BDAY_N.length) : (isDouble() ? 2 : 1); }

  function standPriceCents() {
    var s = STANDS[state.size];
    return s ? s.price : 0;
  }

  function total() {
    var v = variant();
    var cents = v ? v.price : 0;
    if (state.stand) cents += standPriceCents() * standQty();
    if (state.rush && CFG.rushOffered) cents += (CFG.rushFee || 0) * 100;
    return cents;
  }

  /* ---------- date validation ---------- */

  function minDateISO() {
    var d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + (CFG.leadDays || 21));
    while (!BDAY && (d.getDay() === 0 || d.getDay() === 6)) d.setDate(d.getDate() + 1);
    return iso(d);
  }

  function iso(d) {
    return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
  }

  function pretty(isoStr) {
    var p = isoStr.split('-').map(Number);
    return new Date(p[0], p[1] - 1, p[2]).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  }

  function dateProblem(v) {
    if (!v) return '';
    var p = v.split('-').map(Number);
    if (p.length !== 3 || !p[0]) return '';
    var day = new Date(p[0], p[1] - 1, p[2]).getDay();
    if (!BDAY && (day === 0 || day === 6)) return 'Senior night dates run Monday through Friday. Pick a weekday and we’ll build the timeline around it.';
    if (v < minDateISO()) return 'We need at least three weeks. The earliest date we can promise right now is ' + pretty(minDateISO()) + '.';
    return '';
  }

  /* ---------- Cloudinary ---------- */

  function uploadingCount() {
    return state.uploads.filter(function (u) { return u.status === 'uploading'; }).length;
  }

  // 2026-09-19, per Crystal, confirmed live and reproduced on her own machine: the page goes
  // blank white WHILE photos are actively uploading to Cloudinary -- not when the file picker
  // opens, not when it closes, but during the upload itself. Root cause is the same known
  // Chrome GPU-compositor paint bug documented in assets/repaint-guard.js (DOM/CSS confirmed
  // intact throughout -- .page-wrapper reports display:flex, visible, opacity 1 the whole time
  // via getComputedStyle -- this is a pure paint glitch, not a state bug). What's new here is
  // WHEN it happens: repaint-guard.js's own triggers (resize, load, hashchange, visibilitychange,
  // scroll, window focus) all cover discrete moments, but this manifests continuously DURING a
  // sustained burst of DOM/paint churn -- exactly what happens while several photos are mid
  // upload (progress-driven re-renders, thumbnail decode/paint, concurrent XHRs) -- with no
  // single discrete event marking it. Confirmed by hand on Crystal's live browser: neither
  // gentleNudge() nor waiting it out cleared the blank state, but forceRepaint() (the aggressive
  // display:none -> reflow -> restore cycle, already hardened with both a requestAnimationFrame
  // and a hard 120ms setTimeout restore -- see repaint-guard.js) did, immediately.
  //
  // This ties that same, already-safety-netted forceRepaint() directly to actual upload
  // activity instead of waiting on a proxy event: a repeating timer that runs ONLY while at
  // least one upload is genuinely in flight (uploadingCount() > 0), and stops itself the instant
  // none are. 800ms was chosen to be frequent enough that a customer never sits on a blank page
  // for long, without re-running the display:none/restore cycle so often it becomes its own
  // source of visible flicker. Deliberately reads window.__cwForceRepaint (defined by
  // repaint-guard.js) rather than reimplementing the technique here, and no-ops entirely if that
  // function isn't present (e.g. repaint-guard.js failed to load) so this can never throw.
  var uploadRepaintTimer = null;
  function ensureUploadRepaintGuard() {
    if (uploadRepaintTimer) return;
    uploadRepaintTimer = setInterval(function () {
      if (uploadingCount() > 0) {
        if (typeof window.__cwForceRepaint === 'function') window.__cwForceRepaint();
      } else {
        clearInterval(uploadRepaintTimer);
        uploadRepaintTimer = null;
      }
    }, 800);
  }

  // 2026-09-17, per Crystal ("still a huge issue... it cuts off the screen and I can no
  // longer scroll... impossible to finish the order") -- second, separate bug found after
  // fixing the initial batch-add freeze: xhr.upload.onprogress fires many times per file while
  // it uploads, and every progress tick called the full render(), which rebuilds
  // [data-ic-thumbs] from scratch (destroying and recreating every thumbnail's image element).
  // With several real, multi-MB photos uploading at once, that meant dozens of full thumbnail
  // rebuilds per second, forcing the browser to redecode every photo repeatedly -- enough to
  // stall rendering hard, which is what looked like the page "cutting off" and losing scroll.
  // Coalescing progress-driven renders to at most one per animation frame (browsers already
  // paint at most once per frame, so anything more frequent than this was pure waste) fixes it
  // without changing what render() actually does.
  var renderScheduled = false;
  function scheduleRender() {
    if (renderScheduled) return;
    renderScheduled = true;
    requestAnimationFrame(function () {
      renderScheduled = false;
      render();
    });
  }

  // 2026-09-18, per Crystal ("upload is working perfectly - uploading the photos. but then cuts
  // off the page" / "unusable, unclickable white space") -- found with REAL phone photos (the
  // render-throttling fix above was verified with tiny 1x1 test images, which never exercised
  // this). Every thumbnail's background-image was the FULL-RESOLUTION uploaded photo (a real
  // phone photo is commonly 10-40+ megapixels, several MB). Painting even a handful of those as
  // small squares still requires the browser to keep every one of those huge decoded bitmaps
  // resident in memory/GPU texture space -- fine on a spacious desktop, but exactly the kind of
  // memory pressure that can stall or blank a page's rendering on an ordinary customer device.
  // Shrinking each photo to a small on-screen-sized thumbnail ONCE, up front, and displaying that
  // instead removes that pressure regardless of how large the original photo is.
  function makeThumbnail(file, maxDim) {
    if (typeof createImageBitmap !== 'function') return Promise.resolve(null);
    return createImageBitmap(file).then(function (bitmap) {
      var scale = Math.min(maxDim / bitmap.width, maxDim / bitmap.height, 1);
      var w = Math.max(1, Math.round(bitmap.width * scale));
      var h = Math.max(1, Math.round(bitmap.height * scale));
      var canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, w, h);
      if (bitmap.close) bitmap.close();
      return new Promise(function (resolve) {
        if (canvas.toBlob) {
          canvas.toBlob(function (blob) { resolve(blob ? URL.createObjectURL(blob) : null); }, 'image/jpeg', 0.72);
        } else {
          resolve(canvas.toDataURL('image/jpeg', 0.72));
        }
      });
    }).catch(function () { return null; });
  }

  function uploadFiles(fileList) {
    var files = Array.prototype.slice.call(fileList || []);
    if (!files.length) return;

    // 2026-09-17, per Crystal ("I am not a fan of the freezing, I wish we could have an
    // 'uploading, please wait' message instead of uncertainty") -- reproduced the "stuck /
    // blank" page she saw: this loop used to call the full render() (which rebuilds the whole
    // builder UI and re-measures the SVG scale figure via getBBox(), a forced-layout call) once
    // PER FILE. Adding a batch of 10+ photos at once ran that expensive render N times back to
    // back on the main thread with nothing else able to paint in between, which is what looked
    // like a freeze/blank page. Queuing every file first and rendering ONCE after the loop fixes
    // the freeze; the explicit "Uploading…" headline below (driven by uploadingCount()) covers
    // the remaining, normal wait while files actually upload to Cloudinary.
    files.forEach(function (f) {
      if (f.size > 10 * 1024 * 1024) { alert(f.name + ' is over 10 MB. Please send a smaller copy.'); return; }
      var item = { id: 'u' + Date.now() + Math.random(), name: f.name, pct: 0, url: '', status: 'uploading', preview: '' };
      state.uploads.push(item);

      // Downscale in the background; swap the thumbnail in once it's ready. Falls back to the
      // full-size photo only if thumbnailing itself fails, so a photo is never left with no
      // preview at all.
      makeThumbnail(f, 240).then(function (thumbUrl) {
        item.preview = thumbUrl || URL.createObjectURL(f);
        scheduleRender();
      });

      if (!CFG.cloudName || !CFG.uploadPreset) { item.status = 'pending'; return; }

      // 2026-09-19: this item is about to spend real time as a genuine in-flight upload --
      // make sure the compositor-recovery timer above is running for as long as that's true.
      // See the history note on ensureUploadRepaintGuard() for why this exists.
      ensureUploadRepaintGuard();

      var fd = new FormData();
      fd.append('file', f);
      fd.append('upload_preset', CFG.uploadPreset);
      fd.append('folder', (CFG.cloudFolder || 'number-collages') + '/' + REF);
      fd.append('tags', REF + (BDAY ? ',milestone-birthday,birthday-' + BDAY_N : ',' + (CFG.sport || '') + ',senior-night'));
      fd.append('context', [
        'ref=' + REF,
        BDAY ? 'occasion=milestone birthday' : 'sport=' + scrub(CFG.sport),
        (BDAY ? 'birthday_number=' : 'jersey=') + scrub(digitsLabel()),
        'size=' + state.size + ' inch' + (BDAY ? '' : ' ' + (isDouble() ? 'double digit' : 'single digit')),
        'event_date=' + scrub(state.date)
      ].join('|'));

      var xhr = new XMLHttpRequest();
      xhr.open('POST', 'https://api.cloudinary.com/v1_1/' + CFG.cloudName + '/upload');
      xhr.upload.onprogress = function (e) {
        if (e.lengthComputable) { item.pct = Math.round((e.loaded / e.total) * 100); scheduleRender(); }
      };
      xhr.onload = function () {
        var r = null;
        try { r = JSON.parse(xhr.responseText); } catch (_) {}
        if (r && r.secure_url) { item.status = 'done'; item.pct = 100; item.url = r.secure_url; }
        else { item.status = 'error'; }
        scheduleRender();
      };
      xhr.onerror = function () { item.status = 'error'; scheduleRender(); };
      xhr.send(fd);
    });

    render();
  }

  function scrub(v) { return String(v == null ? '' : v).replace(/[|=]/g, ' ').trim(); }

  /* ---------- add to cart ---------- */

  /* Tell the theme's header cart icon / cart drawer / cart-items component that the cart
     changed, so the visible cart UI actually refreshes after a successful add.

     Root cause this fixes (2026-09-19): this builder's fetch to /cart/add.js was succeeding
     the whole time -- the item really was added -- but this file only ever dispatched a
     home-made 'ic:added' CustomEvent afterward. None of the theme's real cart components
     (assets/cart-icon.js, assets/cart-drawer.js, assets/component-cart-items.js) listen for
     that -- they all listen on `document` for the theme's standard cart-update event
     (confirmed by reading those three files directly). So the header cart bubble never
     updated and the drawer never opened, even though the order/line item was genuinely in
     the cart. That silence is what led a customer (and Crystal, testing) to believe nothing
     happened and click Add to Cart again, ending up with the same item added twice.

     This function is purely additive: it never touches whether /cart/add.js succeeded, and
     if anything inside it throws, it's swallowed so the add-to-cart flow itself is completely
     unaffected either way -- it can only add a UI refresh, never take one away.

     Deliberately NOT importing the theme's `@shopify/events` module for this. This file is a
     plain classic script (not type="module"), and after the repaint-guard incident earlier
     today the priority is to add the smallest, most self-contained fix possible rather than
     change how this script loads. Reading assets/cart-icon.js, assets/cart-drawer.js and
     assets/component-cart-items.js directly shows exactly three things they read off the
     event: `event.action`, `event.promise` (which they resolve to get `{ cart, detail }`),
     and that they all attach their listener on `document` -- so no event bubbling is needed
     if we dispatch straight on `document`. Building a plain Event with just those properties
     gets the same result without any dependency on an external module loading. */
  function notifyThemeCartUpdated(addedItems, addResponseBody) {
    try {
      var ev = new Event('shopify:cart:lines-update', { bubbles: true });
      ev.action = 'add';
      ev.context = 'product';
      ev.lines = addedItems.map(function (it) {
        return { merchandiseId: 'gid://shopify/ProductVariant/' + it.id, quantity: it.quantity };
      });

      var addedItemsList = (addResponseBody && addResponseBody.items) || [];
      var fallbackCount = addedItemsList.reduce(function (sum, it) { return sum + (it.quantity || 0); }, 0);

      // Resolve the promise from a fresh /cart.js read so the header bubble shows the
      // TOTAL cart quantity, not just what this add contributed. Never rejects -- if the
      // re-fetch fails, listeners still get a usable (slightly less precise) count instead
      // of hanging.
      ev.promise = fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          return {
            cart: null,
            detail: {
              items: addedItemsList,
              source: 'identity-collage-builder',
              itemCount: (cart && typeof cart.item_count === 'number') ? cart.item_count : fallbackCount,
              didError: false
            }
          };
        })
        .catch(function () {
          return {
            cart: null,
            detail: { items: addedItemsList, source: 'identity-collage-builder', itemCount: fallbackCount, didError: false }
          };
        });

      document.dispatchEvent(ev);
    } catch (e) {
      // Never let a problem here affect the add-to-cart flow itself.
    }
  }

  function addToCart() {
    var v = variant();
    if (!v || state.busy) return;

    var props = {};
    if (BDAY) {
      props['Birthday number'] = digitsLabel();
      props['Party date'] = state.date;
    } else {
      props['Jersey number'] = digitsLabel();
      props['Senior night date'] = state.date;
    }
    var doneUploads = state.uploads.filter(function (u) { return u.status === 'done' && u.url; });
    props['Photos uploaded'] = String(doneUploads.length);
    /* Photo 1 / Photo 2 / etc. cart line-item properties, 2026-09-19 per Crystal: "it shows
       the images uploaded in the cart like this [Photo 1: <url>, Photo 2: <url>, ...], i'd
       like that to stay consistent for the identity collage orders as well" -- matching the
       Senior Night Poster template's pattern exactly (see
       snippets/senior-night-poster-script.liquid). Reverses the original build spec's
       deliberate omission of per-photo URLs from cart properties (truncation-risk concern,
       identity-series-build-spec.md); the poster template has run this same pattern in
       production without issue, so mirroring it here. Each upload's Cloudinary secure_url is
       already tracked as u.url once its status flips to 'done' (see the Cloudinary xhr.onload
       handler above) -- nothing new to fetch. */
    doneUploads.forEach(function (u, i) { props['Photo ' + (i + 1)] = u.url; });
    if (state.share) props['Photo sharing method'] = state.share;
    props['_photo_ref'] = REF;
    props['_photo_folder'] = (CFG.cloudFolder || 'number-collages') + '/' + REF;
    if (state.rush && CFG.rushOffered) props['Rush production'] = 'Yes, ' + money((CFG.rushFee || 0) * 100);

    var items = [{ id: v.id, quantity: 1, properties: props }];
    var stand = STANDS[state.size];
    if (state.stand && stand && stand.id) {
      items.push({ id: stand.id, quantity: standQty(), properties: { 'For collage': SIZES[state.size].label + ' #' + digitsLabel(), '_photo_ref': REF } });
    }

    state.busy = true; render();
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
      .then(function (res) {
        state.busy = false;
        if (!res.ok) { render(res.body && res.body.description ? res.body.description : 'Something went wrong. Please try again.'); return; }
        document.dispatchEvent(new CustomEvent('ic:added', { detail: res.body }));
        notifyThemeCartUpdated(items, res.body);
        if (CFG.redirectToCart) window.location.href = '/cart';
        else render('Added. We’ll email your proof within 3 business days.');
      })
      .catch(function () { state.busy = false; render('Something went wrong. Please try again.'); });
  }

  /* ---------- render ---------- */

  function q(sel) { return root.querySelector(sel); }
  function qa(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function setAttr(sel, attr, value) { var el = q(sel); if (el) el.setAttribute(attr, value); }

  // Bottom-left / bottom-right corners of the number's actual ink, relative to its anchor
  // (text-anchor middle at x, baseline at y). Draws the same text in the same computed font on an
  // offscreen canvas and scans the lowest rows. Returns null if canvas measuring isn't available.
  function inkBottomCorners(textEl, fontSize) {
    try {
      var text = textEl.textContent || '';
      if (!text.trim()) return null;
      var cs = window.getComputedStyle(textEl);
      var font = (cs.fontWeight || '600') + ' ' + fontSize + 'px ' + cs.fontFamily;
      // The jersey font's "5" is a separate, lazily-loaded face (unicode-range) -- the first time a
      // customer types a 5 it isn't downloaded yet, so measure again as soon as it arrives.
      if (document.fonts && document.fonts.check && !document.fonts.check(font, text)) {
        document.fonts.load(font, text).then(function () { updateScale(); }, function () {});
      }
      var c = document.createElement('canvas'), x = c.getContext('2d');
      x.font = font; x.textAlign = 'center';
      var m = x.measureText(text), pad = 10;
      var L = Math.ceil(m.actualBoundingBoxLeft), R = Math.ceil(m.actualBoundingBoxRight);
      var A = Math.ceil(m.actualBoundingBoxAscent), D = Math.ceil(m.actualBoundingBoxDescent || 0);
      if (!(L + R > 0) || !(A > 0)) return null;
      c.width = L + R + pad * 2; c.height = A + D + pad * 2;
      x = c.getContext('2d'); x.font = font; x.textAlign = 'center'; x.fillStyle = '#000';
      var cx = pad + L, by = pad + A; x.fillText(text, cx, by);
      var W = c.width, H = c.height, d = x.getImageData(0, 0, W, H).data, bottom = -1, xx, yy;
      for (yy = H - 1; yy >= 0 && bottom < 0; yy--) for (xx = 0; xx < W; xx++) if (d[(yy * W + xx) * 4 + 3] > 128) { bottom = yy; break; }
      if (bottom < 0) return null;
      var band = Math.max(3, Math.round((A + D) * 0.06)), minX = W, maxX = -1;
      for (yy = Math.max(0, bottom - band); yy <= bottom; yy++) for (xx = 0; xx < W; xx++) if (d[(yy * W + xx) * 4 + 3] > 128) { if (xx < minX) minX = xx; if (xx > maxX) maxX = xx; }
      return { l: minX - cx, r: maxX + 1 - cx, b: bottom + 1 - by };
    } catch (e) { return null; }
  }

  function updateScale() {
    var svg = q('[data-ic-scale-svg]');
    if (!svg) return;

    var sizeIn = parseFloat(state.size) || 15;
    var digitCount = BDAY ? Math.max(1, BDAY_N.length) : (isDouble() ? 2 : 1);

    // Formulas reverse-derived from the designer's five reference SVGs
    // (handoff/scale-figure-{12,15,18,24,36}in.svg) in "Scale Figure Spec", 2026-09-13.
    // Canvas is 460x500, 6px = 1in, figure is 420px tall (5'10" / 70in).
    var collageH = sizeIn * 6;               // collage height in canvas px
    var fontSize = collageH / 0.72;          // measured cap-height ratio
    var gripY = SCALE_GRIP_Y[sizeIn] || 258; // hand-set per size, not a formula -- see spec
    var armCtrlY = gripY - 86;               // arm control points sit 86px above the grip

    // Set the preview number's font-size (and, via render()'s earlier textContent write,
    // its actual digit(s)) BEFORE measuring it below -- the hand/arm placement has to be based
    // on how wide the number really renders, not an estimate.
    setAttr('[data-ic-preview]', 'y', gripY);
    setAttr('[data-ic-preview]', 'font-size', fontSize);

    // 2026-09-15, per Crystal: "those circle hands should cover the edge of the collage, no
    // matter how big it gets; arms and circle hands should adjust in and out." The previous
    // formula (digitCount * fontSize * 0.29) only ever approximated the number's rendered
    // width -- it assumed every digit is the same width, which isn't true across the Superstar/
    // Jersey M54 glyph set (a "1" is much narrower than an "8", and the "5" swap to Jersey M54
    // has yet another metric), so at some size/digit combinations the hands drifted off the
    // actual edge instead of gripping it. Fixed by measuring the live preview text's real
    // rendered bounding box (getBBox) now that its font-size and digits are both set, and
    // gripping exactly at that measured edge -- this self-corrects for every size and every
    // digit combination instead of relying on a single tuned ratio. Falls back to the old
    // estimate only if getBBox is unavailable or the node isn't laid out yet (e.g. a hidden
    // section during theme-editor reordering).
    var halfWidth = digitCount * fontSize * 0.29; // fallback estimate
    var preview = q('[data-ic-preview]');
    if (preview && typeof preview.getBBox === 'function') {
      try {
        var bbox = preview.getBBox();
        if (bbox && bbox.width > 0) { halfWidth = bbox.width / 2; }
      } catch (e) { /* getBBox can throw if the SVG isn't in the render tree yet */ }
    }

    // 2026-09-26, per Crystal: "the circle hands always stay in front of the bottom corners of the
    // number no matter what size." getBBox() is the glyphs' advance box -- wider than the actual ink,
    // and some digits (e.g. "7") narrow toward the foot -- so the hands drifted off the corners on the
    // bigger sizes. Measure the real ink instead (inkBottomCorners(), above) and grip the lowest row's
    // left/right edges, inset so each circle covers the corner. Falls back to the bbox edges.
    var handL = 230 - halfWidth, handR = 230 + halfWidth, handY = gripY;
    var ink = preview ? inkBottomCorners(preview, fontSize) : null;
    if (ink) {
      var HAND_INSET = 6;
      handL = 230 + ink.l + HAND_INSET;
      handR = 230 + ink.r - HAND_INSET;
      handY = gripY + ink.b - HAND_INSET;
    }
    var handArmCtrlY = handY - 86;
    var dimX = Math.min(230 - halfWidth - 26, 150);
    var dimY1 = gripY - collageH, dimY2 = gripY;
    var labelX = dimX - 10, labelY = (dimY1 + dimY2) / 2 + 5;

    svg.setAttribute('aria-label', 'Scale drawing of the ' + sizeIn + ' inch collage held by a 5 foot 10 inch adult');

    setAttr('[data-ic-arm-left]', 'd', 'M180 100 Q160 ' + handArmCtrlY.toFixed(1) + ' ' + handL.toFixed(1) + ' ' + handY.toFixed(1));
    setAttr('[data-ic-arm-right]', 'd', 'M280 100 Q300 ' + handArmCtrlY.toFixed(1) + ' ' + handR.toFixed(1) + ' ' + handY.toFixed(1));
    setAttr('[data-ic-hand-left]', 'cx', handL.toFixed(1));
    setAttr('[data-ic-hand-left]', 'cy', handY.toFixed(1));
    setAttr('[data-ic-hand-right]', 'cx', handR.toFixed(1));
    setAttr('[data-ic-hand-right]', 'cy', handY.toFixed(1));

    setAttr('[data-ic-dim-line]', 'x1', dimX); setAttr('[data-ic-dim-line]', 'x2', dimX);
    setAttr('[data-ic-dim-line]', 'y1', dimY1); setAttr('[data-ic-dim-line]', 'y2', dimY2);
    setAttr('[data-ic-dim-tick-top]', 'x1', dimX - 6); setAttr('[data-ic-dim-tick-top]', 'x2', dimX + 6);
    setAttr('[data-ic-dim-tick-top]', 'y1', dimY1); setAttr('[data-ic-dim-tick-top]', 'y2', dimY1);
    setAttr('[data-ic-dim-tick-bottom]', 'x1', dimX - 6); setAttr('[data-ic-dim-tick-bottom]', 'x2', dimX + 6);
    setAttr('[data-ic-dim-tick-bottom]', 'y1', dimY2); setAttr('[data-ic-dim-tick-bottom]', 'y2', dimY2);

    setAttr('[data-ic-scale-label]', 'x', labelX); setAttr('[data-ic-scale-label]', 'y', labelY);
    setText('[data-ic-scale-label]', sizeIn + '″');

    // 2026-09-15, per Crystal: "i think the mobile layout looks pretty good except it's pushed
    // to the right (not centered)." The figure/head/torso/legs/arms are symmetric around x=230
    // (the middle of the fixed 460-wide canvas), but the dimension line + its "N inch" label only
    // exist on the LEFT side (there's no mirrored element on the right) -- that extra ink makes
    // the drawing's real bounding box lean left of the raw canvas center, which reads as the
    // figure itself sitting right-of-center within its own card. Recenter the viewBox on the
    // SVG's actual rendered bounding box (not the fixed 0-460 assumption) every time this runs,
    // horizontally only, keeping the same 460x500 frame size so nothing rescales -- this keeps it
    // centered at every size and digit combination instead of just the one this was eyeballed on.
    try {
      var full = svg.getBBox();
      if (full && full.width > 0) {
        var vbX = (full.x + full.width / 2) - 230;
        svg.setAttribute('viewBox', vbX.toFixed(1) + ' 0 460 500');
      }
    } catch (e) { /* getBBox can throw if the SVG isn't in the render tree yet */ }

    setText('[data-ic-scale-caption]', 'Drawn to scale against a 5’10″ adult. Switch sizes above to see the ' +
      sizeIn + ' inch collage change in their hands. Sides and back are painted black on every collage.');
  }

  function render(statusOverride) {
    var v = variant();
    var dbl = isDouble();
    var dErr = dateProblem(state.date);
    var done = state.uploads.filter(function (u) { return u.status === 'done' || u.status === 'pending'; }).length;
    var uploading = uploadingCount();

    qa('[data-ic-size]').forEach(function (b) {
      var k = b.getAttribute('data-ic-size');
      b.setAttribute('aria-pressed', k === state.size ? 'true' : 'false');
      var meta = b.querySelector('[data-ic-size-meta]');
      var pv = VARIANTS[k + '|' + state.digits];
      if (meta) meta.textContent = 'Holds ' + photoRange(k, dbl) + ' photos · ' + (pv ? money(pv.price) : '—');
    });

    qa('[data-ic-digits]').forEach(function (b) {
      var k = b.getAttribute('data-ic-digits');
      b.setAttribute('aria-pressed', k === state.digits ? 'true' : 'false');
      var meta = b.querySelector('[data-ic-digits-meta]');
      var pv = VARIANTS[state.size + '|' + k];
      if (meta) meta.textContent = (pv ? money(pv.price) : '—') + ' at ' + SIZES[state.size].label;
    });

    setText('[data-ic-jersey-hint]', dbl
      ? 'Enter your ' + CFG.athleteNoun + '’s number: 10, 30, 00, etc.'
      : 'Enter your ' + CFG.athleteNoun + '’s number or initial: 0, 7, F, etc.');

    var jerseyInput = q('[data-ic-jersey]');
    if (jerseyInput) jerseyInput.placeholder = dbl ? '00' : '0';

    // 2026-09-17, per Crystal ("I am not a fan of the freezing, I wish we could have an
    // 'uploading, please wait' message instead of uncertainty") -- while any photo in the
    // current batch is still mid-upload, say so plainly instead of leaving the headline looking
    // stalled or unchanged.
    setText('[data-ic-photo-headline]', uploading
      ? 'Uploading ' + uploading + ' photo' + (uploading === 1 ? '' : 's') + ', please wait…'
      : (done
        ? done + ' photos added, tap to add more'
        : 'Add ' + photoRange(state.size, dbl) + ' photos'));

    var thumbs = q('[data-ic-thumbs]');
    if (thumbs) {
      thumbs.hidden = state.uploads.length === 0;
      // 2026-09-17: update existing thumb nodes in place (by upload id) instead of nuking and
      // rebuilding the whole grid's innerHTML on every render. With real photos, render() runs
      // very often during upload (once per animation frame while progress is coming in from
      // several files at once); rebuilding every <div style="background-image:url(blob:...)">
      // from scratch each time forces the browser to redecode every photo's image again on
      // every tick. Reusing nodes means only the changed badge text (and any newly added/
      // removed thumb) touches the DOM.
      var existing = {};
      qa('.ic__thumb').forEach(function (el) { existing[el.getAttribute('data-ic-thumb-id')] = el; });
      var seen = {};
      state.uploads.forEach(function (u) {
        seen[u.id] = true;
        var badge = u.status === 'done' ? 'Ready' : u.status === 'error' ? 'Failed' : u.status === 'pending' ? 'Queued' : u.pct + '%';
        var el = existing[u.id];
        if (!el) {
          el = document.createElement('div');
          el.className = 'ic__thumb';
          el.setAttribute('data-ic-thumb-id', u.id);
          el.innerHTML = '<span class="ic__thumb-badge"></span>' +
            '<button type="button" class="ic__thumb-remove" data-ic-remove="' + u.id + '" aria-label="Remove photo">&times;</button>';
          thumbs.appendChild(el);
        }
        // 2026-09-18: the thumbnail preview now arrives asynchronously (see makeThumbnail) --
        // it may not be ready on the render that first creates this node, and it changes exactly
        // once (from empty to the downscaled image), so only touch backgroundImage when the
        // value actually changes rather than reassigning it on every render.
        if (u.preview && el.getAttribute('data-ic-thumb-src') !== u.preview) {
          el.style.backgroundImage = 'url(' + u.preview + ')';
          el.setAttribute('data-ic-thumb-src', u.preview);
        }
        var badgeEl = el.querySelector('.ic__thumb-badge');
        if (badgeEl && badgeEl.textContent !== badge) badgeEl.textContent = badge;
      });
      Object.keys(existing).forEach(function (id) {
        if (!seen[id]) existing[id].remove();
      });
    }

    var warn = q('[data-ic-photo-warning]');
    if (warn) {
      var short = done > 0 && done < photoMin(state.size, dbl);
      warn.hidden = !short;
      if (short) warn.textContent = 'You’ve added ' + done + ' of the ' + photoRange(state.size, dbl) +
        ' photos this size holds. You can check out now and send the rest, or add more above.';
    }

    qa('[data-ic-share]').forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-ic-share') === state.share ? 'true' : 'false');
    });

    var dateInput = q('[data-ic-date]');
    if (dateInput) dateInput.min = minDateISO();
    setText('[data-ic-date-hint]', (BDAY ? 'At least three weeks out. ' : 'Weekdays only, at least three weeks out. ') + 'Earliest right now: ' + pretty(minDateISO()) + '.');
    var dateErr = q('[data-ic-date-error]');
    if (dateErr) { dateErr.hidden = !dErr; dateErr.textContent = dErr; }

    if (BDAY) {
      var sq = standQty(), sqWord = ['a', 'a', 'two', 'three'][sq] || String(sq);
      setText('[data-ic-stand-label]', 'Add ' + sqWord + ' matching ' + SIZES[state.size].label +
        ' stand' + (sq > 1 ? 's' : '') + ', ' + money(standPriceCents() * sq));
      setText('[data-ic-stand-note]', 'Sized automatically to your ' + SIZES[state.size].label + ' collage' +
        (sq > 1 ? ', one stand per digit' : '') + ', so it stands upright on the cake table, buffet or a shelf. ' +
        'No size to pick; change your collage size and the stand' + (sq > 1 ? 's follow.' : ' follows.'));
      setText('[data-ic-summary-line]', SIZES[state.size].label + ' · ' + (CFG.ordinal || BDAY_N) + ' birthday' +
        (state.stand ? ' · ' + (sq > 1 ? sq + ' stands' : 'stand') : ''));
    } else {
    setText('[data-ic-stand-label]', 'Add ' + (dbl ? 'two matching ' : 'a matching ') + SIZES[state.size].label +
      ' stand' + (dbl ? 's' : '') + ', ' + money(standPriceCents() * standQty()));
    setText('[data-ic-stand-note]', 'Sized automatically to your ' + SIZES[state.size].label + ' collage' +
      (dbl ? ', one stand per digit' : '') + ', so it stands upright on a banquet table, entry table or shelf. ' +
      'No size to pick; change your collage size and the stand follows.');

    setText('[data-ic-summary-line]', SIZES[state.size].label + ' · ' + (dbl ? 'double digit' : 'single digit') +
      (state.stand ? ' · ' + (dbl ? '2 stands' : 'stand') : ''));
    }
    setText('[data-ic-total]', money(total()));

    var btn = q('[data-ic-atc]');
    var ready = !!state.jersey && !!state.date && !dErr && !!v && v.available !== false;
    if (btn) {
      btn.disabled = !ready || state.busy;
      btn.textContent = state.busy ? 'Adding…' : 'Add to cart';
    }

    setText('[data-ic-status]', statusOverride || (BDAY ? (
      dErr ? 'Choose a party date we can hit.'
      : !state.date ? 'Add your party date to finish.'
      : (CFG.ordinal || BDAY_N) + ' birthday · ' + SIZES[state.size].label + ' · proof in 3 business days'
    ) : (
      !state.jersey ? 'Add the jersey number or letters to begin.'
      : dErr ? 'Choose a senior night date we can hit.'
      : !state.date ? 'Add your senior night date to finish.'
      : '#' + digitsLabel() + ' · ' + SIZES[state.size].label + ' · proof in 3 business days'
    )));

    var preview = q('[data-ic-preview]');
    if (preview) preview.textContent = digitsLabel();

    updateScale();
  }

  function setText(sel, text) { var el = q(sel); if (el) el.textContent = text; }

  /* ---------- events ---------- */

  root.addEventListener('click', function (e) {
    var t = e.target.closest('[data-ic-size],[data-ic-digits],[data-ic-share],[data-ic-remove],[data-ic-atc]');
    if (!t) return;
    if (t.hasAttribute('data-ic-size')) { state.size = t.getAttribute('data-ic-size'); render(); }
    else if (t.hasAttribute('data-ic-digits')) { state.digits = t.getAttribute('data-ic-digits'); render(); }
    else if (t.hasAttribute('data-ic-share')) {
      var k = t.getAttribute('data-ic-share');
      state.share = state.share === k ? '' : k;
      render();
    } else if (t.hasAttribute('data-ic-remove')) {
      var id = t.getAttribute('data-ic-remove');
      state.uploads = state.uploads.filter(function (u) { return u.id !== id; });
      render();
    } else if (t.hasAttribute('data-ic-atc')) { e.preventDefault(); addToCart(); }
  });

  root.addEventListener('input', function (e) {
    if (e.target.matches('[data-ic-jersey]')) {
      e.target.value = e.target.value.replace(/[^0-9a-zA-Z]/g, '').slice(0, 2).toUpperCase();
      state.jersey = e.target.value;
      render();
    }
  });

  root.addEventListener('change', function (e) {
    if (e.target.matches('[data-ic-date]')) { state.date = e.target.value; render(); }
    else if (e.target.matches('[data-ic-photos]')) { uploadFiles(e.target.files); e.target.value = ''; }
    else if (e.target.matches('[data-ic-stand]')) { state.stand = e.target.checked; render(); }
    else if (e.target.matches('[data-ic-rush]')) { state.rush = e.target.checked; render(); }
  });

  var dz = q('[data-ic-dropzone]');
  if (dz) {
    ['dragenter', 'dragover'].forEach(function (ev) {
      dz.addEventListener(ev, function (e) { e.preventDefault(); dz.classList.add('is-over'); });
    });
    ['dragleave', 'drop'].forEach(function (ev) {
      dz.addEventListener(ev, function (e) { e.preventDefault(); dz.classList.remove('is-over'); });
    });
    dz.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files) uploadFiles(e.dataTransfer.files);
    });
  }

  /* gallery */
  var gMain = q('[data-ic-gallery-main]');
  var gCaption = q('[data-ic-gallery-caption]');
  if (gMain) {
    var setActiveThumb = function (btn) {
      if (!btn) return;
      gMain.src = btn.getAttribute('data-ic-gallery-thumb');
      if (gCaption) {
        var alt = btn.getAttribute('data-ic-gallery-alt') || '';
        gCaption.textContent = alt;
        gCaption.hidden = !alt;
      }
      qa('[data-ic-gallery-thumb]').forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
    };
    root.addEventListener('click', function (e) {
      var t = e.target.closest('[data-ic-gallery-thumb]');
      if (t) { setActiveThumb(t); return; }
      var arrow = e.target.closest('[data-ic-gallery-prev], [data-ic-gallery-next]');
      if (!arrow) return;
      var thumbs = qa('[data-ic-gallery-thumb]');
      if (!thumbs.length) return;
      var idx = thumbs.findIndex(function (b) { return b.getAttribute('aria-pressed') === 'true'; });
      if (idx === -1) idx = 0;
      var step = arrow.hasAttribute('data-ic-gallery-prev') ? -1 : 1;
      setActiveThumb(thumbs[(idx + step + thumbs.length) % thumbs.length]);
    });
  }

  render();
  // 2026-09-26: the hand placement above measures the number in the jersey font -- re-measure once
  // web fonts finish loading, so the first paint isn't measured against a fallback font.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { updateScale(); });
})();